import {useQuery} from '@tanstack/react-query';
import {currency_atom, exchange_name_atom, user_info_atom} from 'atoms';
import {query_balance} from 'connector/account';
import {GET, POST, remove_token, save_token} from 'connector/fetcher';
import {useCallback} from 'react';
import {useNavigate} from 'react-router-dom';
import {useRecoilState, useRecoilValue} from 'recoil';
import {queryClient} from './marketHooks';

export function useAuth() {
	const [user_info, set_user_info] = useRecoilState(user_info_atom);
	const navigate = useNavigate();

	const login = async (params: any): Promise<boolean> => {
		try {
			const resp = await POST('/v1/auth/token', params, 'multipart/form-data');
			if (resp.code === 401) {
				console.error('Login failed: Invalid credentials');
				return false;
			} else if (resp && resp.data && resp.data.access_token) {
				save_token(resp.data.access_token);
				set_user_info({id: resp.data.id, username: resp.data.username});
				return true;
			} else {
				console.error('Login failed: Invalid response', resp);
				return false;
			}
		} catch (error) {
			console.error('Login error:', error);
			return false;
		}
	};

	const logout = () => {
		remove_token();
		set_user_info({id: '', username: ''});
		navigate('/login', {replace: true});
	};

	const relogin = async () => {
		try {
			const resp = await GET('/v1/auth/users/me');
			if (resp.data) set_user_info({id: resp.data.id, username: resp.data.username});
		} catch (error) {
			console.error('Auto login failed:', error);
		}
	};

	const is_logged_in: boolean = !!user_info.id && !!user_info.username;

	return {user_info, login, logout, relogin, is_logged_in};
}

export function useBalance() {
	const exchange = useRecoilValue(exchange_name_atom);
	const currency = useRecoilValue(currency_atom);
	const {is_logged_in} = useAuth();

	const {data, isLoading, isFetching} = useQuery({
		queryKey: ['balance', `${exchange}/${currency}`] as [string, string],
		queryFn: query_balance,
		retry: (failureCount, error) => (error as any).status !== 404 && failureCount < 3,
		retryDelay: 3000,

		refetchOnMount: true,
		refetchOnWindowFocus: true,
		refetchOnReconnect: true,
		refetchIntervalInBackground: false,

		staleTime: 5000,

		enabled: is_logged_in,
	});

	const refresh_forced = useCallback(async () => {
		await queryClient.invalidateQueries({queryKey: ['balance']});
	}, [queryClient]);

	return {balance: data?.balance ?? 0, refresh_forced, isLoading, isFetching};
}
